import { Injectable } from '@angular/core';
import { models } from 'powerbi-client';
import {
  Column,
  ColumnType,
} from '../../../../SGRE-shared/models/tableConfiguration';
import { select, Store } from '@ngrx/store';
import { selectUserLegalEntities } from '../../../../SGRE-shared/store/shared-store/selectors/shared.selector';
import { FormattedData, PowerBIChartData } from '../../model/order-history.model';

@Injectable({
  providedIn: 'root',
})

export class OrderHistoryEffectService {
  constructor(private sharedStore: Store) { }

  createChartsConfig(data: PowerBIChartData) {
    const reportConfig = {
      type: 'report',
      embedUrl: data?.powerBIChart[0]?.embedUrl?.embedUrl,
      tokenType: models.TokenType.Embed,
      accessToken: data?.powerBIChart[0]?.embedToken?.token,
      id: data?.powerBIChart[0]?.embedUrl?.reportId,
      settings: {
        panes: {
          filters: {
            visible: false,
          },
          pageNavigation: {
            visible: false,
          },
        },
      },
    };
    return reportConfig;
  }

  formatData(data, flag: boolean): FormattedData {
    console.log("data", data)
    const results = data?.results?.map((item) =>
      this.mapOrderHistoryData(item)
    );
    data['results'] = results;
    return {
      orderHitoryList: results,
      summary: this.summaryData(data),
      suggestions: this.suggestions(data?.results),
      totalRecords: data?.pagination?.totalResults,
      tableHeader: flag
        ? this.createQuotationTableConfiguration(data)
        : this.createSaleTableConfiguration(data),
      facets: data?.facets,
    };
  }

  mapOrderHistoryData(orderHistoryData) {
    const approvedBy = orderHistoryData?.approvedBy
      ? orderHistoryData?.approvedBy.split('|')[1]
      : '';
    const requestedBy = orderHistoryData?.requestedBy
      ? orderHistoryData?.requestedBy.split('|')[1]
      : '';
    orderHistoryData['approvedBy'] = approvedBy !== 'null' ? approvedBy : '';
    orderHistoryData['companyPo'] = orderHistoryData?.companySo;
    orderHistoryData['navigateUrlId'] = orderHistoryData?.orderId;
    orderHistoryData['sgreSo'] = orderHistoryData?.sgreSo
      ? orderHistoryData?.sgreSo
      : '';
    orderHistoryData['requestedBy'] = requestedBy !== 'null' ? requestedBy : '';
    orderHistoryData['requestedDate'] = orderHistoryData?.orderDate;
    orderHistoryData['orderStatus'] = this.mapOrderStatus(
      orderHistoryData?.orderStatus
    );
    orderHistoryData['details'] = 'Show order';
    orderHistoryData['legalEntity'] = orderHistoryData?.legalEntity?.name;
    orderHistoryData['word-break'] =
      orderHistoryData.companyPo !== 'null' ? true : false;
    return orderHistoryData;
  }

  mapOrderStatus(status) {
    if (status === 'A') {
      return 'Not yet processed';
    } else if (status === 'B') {
      return 'Partially processed';
    } else if (status === 'C') {
      return 'Completely processed';
    } else if (status === 'PENDING_APPROVAL') {
      return 'Pending approval';
    } else if (status === 'CANCELLED') {
      return 'Cancelled';
    } else if (status === 'REJECTED') {
      return 'Rejected';
    } else if (status === 'CANCELLING') {
      return 'Cancelling';
    } else if (status === 'APPROVED') {
      return 'Approved';
    }
    return status;
  }

  createSaleTableConfiguration(data): Column[] {
    const filterCompanyList = data?.facets
      ?.filter((value) => value.name === 'company')
      ?.at(0)
      ?.values?.map((value) => ({
        company: value?.name,
        selected: value?.selected,
      }));
    const filterLegalEntities = data?.facets
      ?.filter((value) => value.name === 'legalEntity')
      ?.at(0)
      ?.values?.map((value) => ({
        legalEntity: this.findLegalEntityName(value?.name),
        selected: value?.selected,
      }));
    const filterRequestedBy = data?.facets
      ?.filter((value) => value.name === 'requestedBy')
      ?.at(0)
      ?.values?.map((value) => ({
        requestedBy: value?.name?.split('|')?.at(1),
        selected: value?.selected,
        value: value?.name,
      }));
    const filterApprovedBy = data?.facets
      ?.filter((value) => value.name === 'approver')
      ?.at(0)
      ?.values?.map((value) => ({
        approvedBy: value?.name?.split('|')?.at(1),
        selected: value?.selected,
        value: value?.name,
      }));
    const filterOrderStatus = data?.facets
      ?.filter((value) => value.name === 'status')
      ?.at(0)
      ?.values?.map((value) => ({
        orderStatus: this.mapOrderStatus(value.name),
        selected: value?.selected,
      }));
    const columnData = [
      {
        label: 'Company',
        name: 'company',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterCompanyList,
      },
      {
        label: 'Legal Entity',
        name: 'legalEntity',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterLegalEntities,
      },
      { label: 'SGRE SO', name: 'sgreSo', type: ColumnType.text },
      { label: 'Company PO', name: 'companyPo', type: ColumnType.text },
      {
        label: 'Requested By',
        name: 'requestedBy',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterRequestedBy,
      },
      {
        label: 'Approved By',
        name: 'approvedBy',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterApprovedBy,
      },
      { label: 'Order Date', name: 'orderDate', type: ColumnType.date },
      {
        label: 'Order Total',
        name: 'orderTotal',
        type: ColumnType.price,
        align: 'left',
      },
      {
        label: 'Order Status',
        name: 'orderStatus',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterOrderStatus,
      },
      { label: 'Details', name: 'details', type: ColumnType.navigate },
    ];
    const salesColumnHeader = columnData.map((item) => new Column(item));
    return salesColumnHeader;
  }

  createQuotationTableConfiguration(data): Column[] {
    const filterCompanyList = data?.facets
      ?.filter((value) => value.name === 'company')
      ?.at(0)
      ?.values?.map((value) => ({
        company: value?.name,
        selected: value?.selected,
      }));
    const filterLegalEntities = data?.facets
      ?.filter((value) => value.name === 'legalEntity')
      ?.at(0)
      ?.values?.map((value) => ({
        legalEntity: this.findLegalEntityName(value?.name),
        selected: value?.selected,
      }));
    const filterRequestedBy = data?.facets
      ?.filter((value) => value.name === 'requestedBy')
      ?.at(0)
      ?.values?.map((value) => ({
        requestedBy: value?.name?.split('|')?.at(1),
        selected: value?.selected,
        value: value?.name,
      }));
    const filterApprovedBy = data?.facets
      ?.filter((value) => value.name === 'approver')
      ?.at(0)
      ?.values?.map((value) => ({
        approvedBy: value?.name?.split('|')?.at(1),
        selected: value?.selected,
        value: value?.name,
      }));
    const columnData = [
      {
        label: 'Company',
        name: 'company',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterCompanyList,
      },
      {
        label: 'Legal Entity',
        name: 'legalEntity',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterLegalEntities,
      },
      { label: 'SGRE SO', name: 'sgreSo', type: ColumnType.text },
      { label: 'Company PO', name: 'companyPo', type: ColumnType.text },
      {
        label: 'Requested By',
        name: 'requestedBy',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterRequestedBy,
      },
      {
        label: 'Approved By',
        name: 'approvedBy',
        type: ColumnType.text,
        filter: true,
        filterOptions: filterApprovedBy,
      },
      { label: 'Requested Date', name: 'requestedDate', type: ColumnType.date },
      { label: 'Details', name: 'details', type: ColumnType.navigate },
    ];
    const QuatationColumn = columnData.map((item) => new Column(item));
    return QuatationColumn;
  }

  findLegalEntityName(uid) {
    let legalEntities = [];
    this.sharedStore
      .pipe(select(selectUserLegalEntities))
      .subscribe((userLegalEntities) => {
        legalEntities = Array.isArray(userLegalEntities)
          ? userLegalEntities.map((item) => item)
          : [];
      });
    return legalEntities?.find((item) => item.uid === uid)?.name;
  }

  summaryData(data) {
    const totalOrders = data?.pagination?.totalResults;
    const totalList = data?.totals
      ? data?.totals.map((item) => {
        const index = this.getIndexCurrency(item.currency);
        item['active'] = index === 0 || index === 2;
        return item;
      })
      : [];
    const stringCheck: string = data?.currentQuery?.query?.value;
    const isQuotation = stringCheck?.includes('isQuotation:true');
    return { totalOrders, totalList, isQuotation };
  }

  getIndexCurrency(currency) {
    const currencies = ['EUR', 'DKK', 'USD', 'GBP', 'CAD'];
    return currencies.indexOf(currency);
  }

  suggestions(data) {
    if (data) {
      const companyPO = data
        ? data.map((item) => item.companyPo).filter((str) => str && str !== '')
        : [];
      const sgreSO = data
        ? data.map((item) => item.sgreSo).filter((str) => str && str !== '')
        : [];
      return Array.from(new Set([...companyPO, ...sgreSO]));
    }
    return [];
  }
}
